import { useEffect, useState } from "react";
import { siteStatus, siteUnlock } from "@/lib/api";
import { useSiteContent } from "@/context/SiteContentContext";

/**
 * Pre-launch site gate.
 *
 * Wraps the entire app. On mount it calls `/api/site/status`:
 *   • locked === false  →  render children (site is public/live)
 *   • locked === true   →  show password screen until the visitor unlocks
 *
 * Once the visitor enters the correct password, the backend hands back
 * an opaque token which we persist to `localStorage` (`gl_site_unlock`).
 * The axios interceptors in `lib/api.js` attach that token as
 * `X-Site-Unlock` on every subsequent /api/* request, so backend
 * middleware allows those calls through.
 *
 * Flip the gate off any time by setting `SITE_LOCKED=false` on Render.
 * The already-issued localStorage tokens remain valid — they just
 * become no-ops.
 */
const UNLOCK_KEY = "gl_site_unlock";

const SiteGate = ({ children }) => {
  const [state, setState] = useState("checking"); // checking | open | locked
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { refresh: refreshSiteContent } = useSiteContent();

  // Whenever the gate flips to "open" — either because the site was
  // already public, we honoured a previously-stored unlock token, or the
  // visitor just entered the password — nudge the site-content cache to
  // refetch. SiteContentProvider's initial fetch fires BEFORE unlock, so
  // it 401s and leaves the store at defaults; without this refresh the
  // admin-configured hero image, announcement bar, category tiles, etc.
  // wouldn't appear until the next full page reload.
  useEffect(() => {
    if (state === "open") {
      refreshSiteContent?.();
    }
  }, [state, refreshSiteContent]);

  useEffect(() => {
    let alive = true;
    const boot = async () => {
      try {
        const s = await siteStatus();
        if (!alive) return;
        if (!s.locked) {
          setState("open");
          return;
        }
        // Site is locked — check if we already unlocked in a prior session.
        if (localStorage.getItem(UNLOCK_KEY)) {
          setState("open");
          return;
        }
        setState("locked");
      } catch (err) {
        // If /site/status fails (network hiccup, cold-starting Render dyno,
        // CORS misconfig) default to LOCKED — better to show the password
        // screen than to render a broken storefront where every API call
        // 401s. If localStorage already has a token, honour it.
        if (!alive) return;
        if (localStorage.getItem(UNLOCK_KEY)) {
          setState("open");
        } else {
          setState("locked");
        }
      }
    };
    boot();
    return () => { alive = false; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setSubmitting(true);
    try {
      const r = await siteUnlock(password);
      if (r?.token) {
        localStorage.setItem(UNLOCK_KEY, r.token);
      }
      setState("open");
    } catch (err) {
      setError(err?.response?.data?.detail || "Wrong password. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (state === "checking") {
    return (
      <div className="min-h-screen bg-[var(--gl-bg)] flex items-center justify-center">
        <div className="font-badge text-[10px] tracking-[0.32em] text-[var(--gl-orange-hover)] uppercase animate-pulse">
          GOYA LISBOA
        </div>
      </div>
    );
  }
  if (state === "open") return children;

  return (
    <div
      data-testid="site-gate"
      className="min-h-screen bg-[var(--gl-yellow)] flex items-center justify-center px-6 py-12 relative overflow-hidden"
    >
      {/* Decorative background — soft grain + a large italic serif "coming soon" watermark */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-[0.06] select-none"
      >
        <div className="font-serif-display italic text-[26vw] leading-none text-[#241C18]">
          soon
        </div>
      </div>

      <div className="relative z-10 w-full max-w-md text-center">
        <div className="font-serif-display text-4xl md:text-5xl tracking-tight text-[#241C18]">GOYA</div>
        <div className="font-badge text-[10px] tracking-[0.5em] text-[#241C18] mt-1">— LISBOA —</div>

        <div className="mt-14">
          <div className="font-badge text-[10px] tracking-[0.32em] text-[#241C18]/70 uppercase">
            Pre-launch preview
          </div>
          <h1 className="font-serif-display text-3xl md:text-4xl mt-3 text-[#241C18] leading-tight">
            We're getting dressed.
          </h1>
          <p className="mt-3 text-sm text-[#241C18]/80 leading-relaxed">
            The boutique opens soon. Enter the pre-launch password to take a peek.
          </p>
        </div>

        <form onSubmit={submit} className="mt-8 flex flex-col gap-3" data-testid="site-gate-form">
          <input
            type="password"
            autoFocus
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            data-testid="site-gate-input"
            className="w-full h-12 px-4 bg-white/95 border border-[#241C18]/20 rounded-full text-center font-badge tracking-[0.18em] text-[#241C18] placeholder:text-[#241C18]/40 focus:outline-none focus:border-[#241C18]"
          />
          <button
            type="submit"
            disabled={submitting || !password.trim()}
            data-testid="site-gate-submit"
            className="h-12 px-6 rounded-full bg-[#241C18] text-[var(--gl-yellow)] font-badge text-[11px] tracking-[0.28em] uppercase disabled:opacity-50 transition hover:bg-[#3A2D24]"
          >
            {submitting ? "Unlocking…" : "Enter →"}
          </button>
          {error && (
            <div data-testid="site-gate-error" className="mt-2 text-xs text-[#8B1A1A]">
              {error}
            </div>
          )}
        </form>

        <div className="mt-14 text-xs text-[#241C18]/60">
          <a
            href="mailto:goyalisboapt@gmail.com"
            className="underline underline-offset-4 hover:text-[#241C18]"
          >
            goyalisboapt@gmail.com
          </a>
          {"  ·  "}
          <a
            href="https://www.instagram.com/goyalisboa"
            target="_blank"
            rel="noreferrer"
            className="underline underline-offset-4 hover:text-[#241C18]"
          >
            @goyalisboa
          </a>
        </div>
      </div>
    </div>
  );
};

export default SiteGate;
